import "@vojomo-core/shared/src/static/styles/App.css";
import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Routes, Route, useLocation } from "react-router-dom";
import {
  clearCookies,
  NotificationsProvider,
  RouteConfigProps,
  SpendingLimitProvider,
  useAccount
} from "@vojomo-core/shared";
import { routeConfig } from "./config/routeConfig";
import { Unauthorized } from "./pages/Errors/Unauthorized";
import { BusinessSignup } from "./pages/BusinessSignup/BusinessSignup";
import { AdminActAsBar } from "./components/AdminActAs/AdminActAsBar";
import BaseLayout from "./layouts/BaseLayout";
import { SpendingLimitModal } from "./components/SpendingLimit/SpendingLimitModal";
import { TopicViewerModal } from "components/TopicViewerModal/TopicViewerModal";

// Import all .jsx files from the ./pages directory
const pageComps: any = [];
const pages = import.meta.glob("./pages/**/*.*sx", { eager: true });
for (const path in pages) {
  pageComps.push(pages[path]);
}

// Matches `/roadmap/build_roadmap/<id>` but NOT
// `/roadmap/build_roadmap/invite/...`, `/join/...`, `/contribute/...`.
const CANONICAL_ROADMAP_PATTERN = /^\/roadmap\/build_roadmap\/[^/]+$/;

/**
 * Logged-out visitors who hit the canonical roadmap URL (e.g. an emailed
 * link) should be sent through Auth0 with the original URL preserved as
 * the returnTo, so post-login they land on the roadmap — not on `/`.
 *
 * use-account.tsx will fire `login()` for any non-public route anyway,
 * but we make it explicit here so the App.tsx fall-through is a real
 * branch ("redirecting to login") rather than a generic loading state.
 */
function CanonicalRoadmapLoginGate() {
  const { login } = useAccount();
  useEffect(() => {
    login();
  }, []);
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: "16px",
        color: "#586B8F"
      }}
    >
      Redirecting to login…
    </div>
  );
}

function App() {
  const { vojomoAccount, userUnauthorized } = useAccount();
  const { isAuthenticated, isLoading: auth0Loading } = useAuth0();
  const location = useLocation();

  // Public route: podcast share pages are accessible without authentication
  if (location.pathname.startsWith("/studio/audio/podcasts/share/")) {
    const PodcastShareComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "PodcastShare"
    )?.PodcastShare;
    if (PodcastShareComp) {
      return (
        <Routes>
          <Route
            path="/studio/audio/podcasts/share/:token"
            element={<PodcastShareComp />}
          />
        </Routes>
      );
    }
  }

  // Render app
  if (vojomoAccount) {
    if (!vojomoAccount.company) {
      return (
        <>
          <AdminActAsBar />
          <BusinessSignup />
        </>
      );
    }

    const InviteComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "RoadmapBuilderInviteAccept"
    )?.RoadmapBuilderInviteAccept;

    const JoinComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "RoadmapBuilderJoin"
    )?.RoadmapBuilderJoin;

    return (
      <NotificationsProvider>
        <SpendingLimitProvider>
          <AdminActAsBar />
          <TopicViewerModal />
          <SpendingLimitModal />
          <Routes>
            {/* Legacy invite accept page (old email links) */}
            {InviteComp && (
              <Route
                path="/roadmap/build_roadmap/invite/:token"
                element={<InviteComp />}
              />
            )}
            {/* New join page: auto-accepts and redirects, no intermediate step */}
            {JoinComp && (
              <Route
                path="/roadmap/build_roadmap/join/:token"
                element={<JoinComp />}
              />
            )}
            {routeConfig.map(function (comp: RouteConfigProps) {
              const CompComponent = pageComps.find(
                (pageComp) => Object.keys(pageComp)[0] === comp.component
              )?.[comp.component];

              // Skip if component not found
              if (!CompComponent) {
                return null;
              }

              if (
                comp.title === "Unauthorized" ||
                comp.title === "Vojomo" ||
                comp.title == "Beta Launch"
              ) {
                return (
                  <Route
                    key={comp.key}
                    path={comp.path}
                    element={<CompComponent />}
                  />
                );
              }

              return (
                <Route
                  key={comp.key}
                  path={comp.path}
                  element={
                    <BaseLayout pageTitle={comp.title}>
                      <CompComponent />
                    </BaseLayout>
                  }
                />
              );
            })}
          </Routes>
        </SpendingLimitProvider>
      </NotificationsProvider>
    );
  } else if (!vojomoAccount && location.pathname === "/beta_launch") {
    // Allow unauthenticated access to Beta Launch page
    const BetaLaunchComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "BetaLaunch"
    )?.BetaLaunch;

    return (
      <Routes>
        <Route path="/beta_launch" element={<BetaLaunchComp />} />
      </Routes>
    );
  } else if (
    !vojomoAccount &&
    location.pathname.startsWith("/roadmap/build_roadmap/invite/")
  ) {
    // Allow unauthenticated access to roadmap invite accept page (legacy links)
    const InviteComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "RoadmapBuilderInviteAccept"
    )?.RoadmapBuilderInviteAccept;

    return (
      <Routes>
        <Route
          path="/roadmap/build_roadmap/invite/:token"
          element={<InviteComp />}
        />
      </Routes>
    );
  } else if (
    !vojomoAccount &&
    location.pathname.startsWith("/roadmap/build_roadmap/join/")
  ) {
    // Allow unauthenticated access to new join page — it triggers loginWithRedirect internally
    const JoinComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "RoadmapBuilderJoin"
    )?.RoadmapBuilderJoin;

    return (
      <Routes>
        <Route
          path="/roadmap/build_roadmap/join/:token"
          element={<JoinComp />}
        />
      </Routes>
    );
  } else if (
    !vojomoAccount &&
    location.pathname.startsWith("/roadmap/build_roadmap/contribute/")
  ) {
    // Allow unauthenticated access to token-gated progress contribution page
    const ContributeComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "ContributeProgress"
    )?.ContributeProgress;

    return (
      <Routes>
        <Route
          path="/roadmap/build_roadmap/contribute/:roundId/:token"
          element={<ContributeComp />}
        />
      </Routes>
    );
  } else if (!vojomoAccount && location.pathname.startsWith("/roadmap/view/")) {
    // Allow unauthenticated access to public shared roadmap view
    const ShareViewComp = pageComps.find(
      (pageComp) => Object.keys(pageComp)[0] === "RoadmapBuilderShareView"
    )?.RoadmapBuilderShareView;

    return (
      <Routes>
        <Route path="/roadmap/view/:token" element={<ShareViewComp />} />
      </Routes>
    );
  } else if (
    !vojomoAccount &&
    !isAuthenticated &&
    !auth0Loading &&
    CANONICAL_ROADMAP_PATTERN.test(location.pathname)
  ) {
    // Logged-out visit to /roadmap/build_roadmap/:id (typically an emailed
    // link). Send through Auth0 with returnTo=current URL so we land back
    // on the roadmap, not on `/`.
    //
    // Gated on Auth0's `isAuthenticated=false && isLoading=false` so we
    // don't fire login() during the post-callback window where Auth0 has
    // a session but use-account.verifyUser hasn't finished resolving the
    // app-side `vojomoAccount` yet — that race produces an infinite
    // login redirect loop.
    return <CanonicalRoadmapLoginGate />;
  } else if (userUnauthorized) {
    clearCookies();

    return <Unauthorized />;
  }

  // Loading state while authentication is in progress
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: "18px",
        color: "#586B8F"
      }}
    >
      Loading...
    </div>
  );
}

export default App;
